/**
 * Browser audio of the player: the French voice of the virtual examiner (speechSynthesis), the
 * microphone level meter and the recorder (MediaRecorder, audio/webm or audio/mp4 on Safari),
 * and the exam audio heard once (D-114: never lost to a blocked autoplay, a network cut or a
 * hidden tab).
 */

import { speechChunks, speechWatchdogMs } from "@agencyos/core";
import { useEffect, useRef, useState } from "react";

export function speechAvailable(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

export function deviceVoices(): Promise<SpeechSynthesisVoice[]> {
  const synth = window.speechSynthesis;
  const now = synth.getVoices();
  if (now.length) return Promise.resolve(now);
  return new Promise((resolve) => {
    const done = () => resolve(synth.getVoices());
    synth.addEventListener?.("voiceschanged", done, { once: true });
    window.setTimeout(done, 1200); // some browsers never fire the event
  });
}

/** The best French voice: fr-FR first, then fr-CA, then any French one. */
export function pickFrenchVoice(list: SpeechSynthesisVoice[]): SpeechSynthesisVoice | undefined {
  const lang = (v: SpeechSynthesisVoice) => v.lang.replace("_", "-").toLowerCase();
  return (
    list.find((v) => lang(v) === "fr-fr") ??
    list.find((v) => lang(v) === "fr-ca") ??
    list.find((v) => lang(v).startsWith("fr"))
  );
}

export interface UtterOptions {
  /** BCP 47 code of the text (« fr-FR », « en-GB »), used when no voice is given. */
  lang: string;
  voice?: SpeechSynthesisVoice;
  rate?: number;
  pitch?: number;
}

export type UtterResult = "ok" | "blocked" | "stopped";

// Chrome drops the events of an utterance nobody references any more (it never ends): the
// utterances being spoken are kept here until they are over.
const speaking = new Set<SpeechSynthesisUtterance>();
let generation = 0; // a new reading (or a stop) ends the current one

/**
 * Reads a text once with the device's voice and resolves when it is over. Never hangs (D-114):
 * the text is read in short pieces (Chrome stops a long utterance without an « end » event) and
 * each piece has a watchdog. « blocked »: the browser refused to speak (no user gesture yet).
 */
export async function utter(text: string, options: UtterOptions): Promise<UtterResult> {
  if (!speechAvailable() || !text.trim()) return "ok";
  const synth = window.speechSynthesis;
  const mine = generation;
  for (const [n, piece] of speechChunks(text).entries()) {
    if (mine !== generation) return "stopped";
    const outcome = await new Promise<UtterResult>((resolve) => {
      const utterance = new SpeechSynthesisUtterance(piece);
      utterance.lang = options.voice?.lang ?? options.lang;
      if (options.voice) utterance.voice = options.voice;
      utterance.rate = options.rate ?? 0.95;
      utterance.pitch = options.pitch ?? 1;
      let settled = false;
      const finish = (result: UtterResult) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(watchdog);
        speaking.delete(utterance);
        resolve(result);
      };
      const watchdog = window.setTimeout(() => finish("ok"), speechWatchdogMs(piece, utterance.rate));
      utterance.onend = () => finish("ok");
      utterance.onerror = (event: SpeechSynthesisErrorEvent) =>
        finish(event.error === "not-allowed" ? "blocked" : event.error === "interrupted" || event.error === "canceled" ? "stopped" : "ok");
      speaking.add(utterance);
      if (synth.paused) synth.resume(); // a voice left paused (hidden tab) would never start
      synth.speak(utterance);
    });
    if (outcome === "blocked") return n === 0 ? "blocked" : "stopped";
    if (outcome === "stopped") return "stopped";
  }
  return mine === generation ? "ok" : "stopped";
}

/** Stops the device's voice and every reading waiting for it. */
export function stopSpeaking(): void {
  generation += 1;
  if (speechAvailable()) window.speechSynthesis.cancel();
}

/** Read a French text aloud once; resolves when finished (or at once without speech support). */
export async function speakFrench(text: string, rate = 0.95): Promise<void> {
  if (!speechAvailable() || !text.trim()) return;
  const voice = pickFrenchVoice(await deviceVoices());
  stopSpeaking();
  await utter(text, { lang: "fr-FR", voice, rate });
}

const MIC_SILENT_LEVEL = 0.01;

export function recorderMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) ?? "";
}

/**
 * Options of a whole-task recorder (D-171). Speech does not need the browser's default bit rate
 * (often 128 kbit/s): 270 s then weigh 4 MB, which a slow connection takes minutes to send.
 */
export function recorderOptions(mimeType: string): MediaRecorderOptions {
  return { ...(mimeType ? { mimeType } : {}), audioBitsPerSecond: 48_000 };
}

const SEND_PAUSES_MS = [2_000, 5_000, 15_000];

/**
 * Sends a recording, trying again by itself when the network or the server failed (D-171): the
 * blob only lives in this page, so it must not depend on the learner pressing « retry ».
 * An answer of the server that is a refusal (4xx) is final.
 */
export async function sendRecording<T>(send: () => Promise<T>, pauses: number[] = SEND_PAUSES_MS): Promise<T> {
  for (const pause of pauses) {
    try {
      return await send();
    } catch (failure) {
      const status = (failure as { status?: unknown } | null)?.status;
      if (typeof status === "number" && status >= 400 && status < 500) throw failure;
      await new Promise((resolve) => window.setTimeout(resolve, pause));
    }
  }
  return send();
}

export function extensionFor(mime: string): string {
  if (mime.includes("mp4")) return "m4a";
  if (mime.includes("ogg")) return "ogg";
  return "webm";
}

/** Level 0..1 of a microphone stream, refreshed about 10 times a second. */
export function useMicLevel(stream: MediaStream | null): number {
  const [level, setLevel] = useState(0);
  useEffect(() => {
    if (!stream) return;
    const AudioCtx = window.AudioContext;
    if (!AudioCtx) return;
    const context = new AudioCtx();
    // Safari (and Chrome without a recent gesture) creates it « suspended »: the meter, the
    // end of a turn and the examiner's relaunch would never hear a voice (D-114).
    const wake = () => {
      if (context.state === "suspended") void context.resume().catch(() => undefined);
    };
    wake();
    document.addEventListener("pointerdown", wake);
    document.addEventListener("keydown", wake);
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    const source = context.createMediaStreamSource(stream);
    source.connect(analyser);
    const buffer = new Uint8Array(analyser.fftSize);
    const timer = window.setInterval(() => {
      analyser.getByteTimeDomainData(buffer);
      let sum = 0;
      for (const sample of buffer) sum += ((sample - 128) / 128) ** 2;
      setLevel(Math.min(1, Math.sqrt(sum / buffer.length) * 4));
    }, 100);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("pointerdown", wake);
      document.removeEventListener("keydown", wake);
      source.disconnect();
      void context.close();
    };
  }, [stream]);
  return level;
}

/** Calls `onSilence` each time the level stays under the threshold for `seconds`. */
export function useSilence(level: number, active: boolean, seconds: number, onSilence: () => void): void {
  const since = useRef<number | null>(null);
  const callback = useRef(onSilence);
  useEffect(() => {
    callback.current = onSilence;
  }, [onSilence]);
  useEffect(() => {
    if (!active) {
      since.current = null;
      return;
    }
    const now = Date.now();
    if (level > 0.06) {
      since.current = now;
    } else if (since.current === null) {
      since.current = now;
    } else if (now - since.current >= seconds * 1000) {
      since.current = now;
      callback.current();
    }
  }, [level, active, seconds]);
}

/** D-175: true while the level has stayed near zero for `seconds`: nothing is being recorded. */
export function useMicStayedSilent(level: number, active: boolean, seconds = 10): boolean {
  const [silent, setSilent] = useState(false);
  const since = useRef<number | null>(null);
  useEffect(() => {
    if (!active || level > MIC_SILENT_LEVEL) {
      since.current = null;
      setSilent(false);
      return;
    }
    const now = Date.now();
    since.current ??= now;
    if (now - since.current >= seconds * 1000) setSilent(true);
  }, [level, active, seconds]);
  return silent;
}

export async function openMicrophone(): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) throw new Error("no-microphone");
  return navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
}

export function closeStream(stream: MediaStream | null): void {
  stream?.getTracks().forEach((track) => track.stop());
}

// --- One media element at a time -----------------------------------------------------------------

let current: HTMLMediaElement | null = null;

/** A player starts: the one playing before stops (two documents are never heard together). */
export function claimPlayback(element: HTMLMediaElement): void {
  if (current && current !== element && typeof current.pause === "function") current.pause();
  current = element;
}

// --- The exam audio, heard once -------------------------------------------------------------------

/** 0.05 s of silence (WAV): played in a user gesture, it unlocks the element on iOS Safari. */
const SILENCE =
  "data:audio/wav;base64,UklGRrQBAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YZABAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICA";

let shared: HTMLAudioElement | null = null;
let unlocked = false;

/** The page's exam audio element: the same one for every document, so that the gesture that
 * unlocked it on iOS still counts when the next document starts by itself. */
function sharedElement(): HTMLAudioElement {
  if (!shared || !(shared instanceof Audio)) {
    shared = new Audio();
    shared.preload = "auto";
  }
  return shared;
}

/** Unlocks the exam audio in a user gesture (a click, a key): iOS Safari then lets the page
 * start the next documents by itself. Harmless when called again. */
export function unlockExamAudio(): void {
  if (unlocked || typeof Audio === "undefined") return;
  unlocked = true;
  const element = sharedElement();
  if (element.src && element.src !== SILENCE) return; // a document is already loaded
  element.src = SILENCE;
  const attempt = element.play?.();
  if (attempt && typeof attempt.then === "function") {
    attempt
      .then(() => {
        if (element.src === SILENCE) element.pause(); // never a document started meanwhile
      })
      .catch(() => {
        unlocked = false; // not a real gesture: tried again on the next one
      });
  }
}

let listening = false;
/** The first gesture on the page unlocks the exam audio (installed once by the players). */
export function unlockOnFirstGesture(): void {
  if (listening || typeof document === "undefined") return;
  listening = true;
  const once = () => {
    unlockExamAudio();
    if (unlocked) {
      document.removeEventListener("pointerdown", once, true);
      document.removeEventListener("keydown", once, true);
    }
  };
  document.addEventListener("pointerdown", once, true);
  document.addEventListener("keydown", once, true);
}

export type OnceOutcome = "playing" | "blocked" | "failed";

interface OnceHandlers {
  onEnded: () => void;
  /** The document could not be heard to its end (network lost for good, file missing). */
  onFailed: () => void;
}

const RETRIES = 3;
const STALL_MS = 12000;

/**
 * A document heard ONCE (D-106) from its signed link, which the server gives only once. The
 * same link is used again when the browser blocks the start (« blocked »: the learner clicks),
 * when the network drops (reloaded from where it stopped, 3 times), or when the tab comes back
 * (a hidden tab paused by the system). Never a second listening, never a lost one (D-114).
 */
export class OnceAudio {
  private element: HTMLAudioElement | null = null;
  private url = "";
  private position = 0;
  private retries = 0;
  private finished = false;
  private lastMove = 0;
  private watch: number | undefined;
  private readonly visibility = () => {
    if (document.visibilityState === "visible") this.wake();
  };

  constructor(private readonly handlers: OnceHandlers) {}

  /** Starts the document (in a gesture if possible). */
  async start(url: string): Promise<OnceOutcome> {
    this.url = url;
    const element = sharedElement();
    this.element = element;
    element.onended = () => this.end(true);
    element.onerror = () => void this.recover();
    element.ontimeupdate = () => {
      if (typeof element.currentTime === "number" && element.currentTime > this.position) {
        this.position = element.currentTime;
        this.lastMove = Date.now();
      }
    };
    element.src = url;
    document.addEventListener("visibilitychange", this.visibility);
    this.watch = window.setInterval(() => this.checkStall(), 2000);
    return this.play();
  }

  /** The learner clicks « Listen » after a blocked start: the same element, the same link. */
  resume(): Promise<OnceOutcome> {
    unlockExamAudio();
    return this.play();
  }

  stop(): void {
    this.finished = true;
    this.cleanup();
    this.element?.pause?.();
  }

  private async play(): Promise<OnceOutcome> {
    const element = this.element;
    if (!element || this.finished) return "failed";
    claimPlayback(element);
    this.lastMove = Date.now();
    try {
      await element.play();
      unlocked = true;
      return "playing";
    } catch (failure) {
      if (failure instanceof DOMException && failure.name === "NotAllowedError") return "blocked";
      if (failure instanceof DOMException && failure.name === "AbortError") return "playing"; // reloaded meanwhile
      if (await this.recover()) return "playing";
      this.end(false);
      return "failed";
    }
  }

  /** The tab is visible again, or the system paused the audio: it goes on where it was. */
  private wake(): void {
    const element = this.element;
    if (!element || this.finished || element.ended) return;
    if (element.paused) void element.play()?.catch(() => undefined);
  }

  private checkStall(): void {
    const element = this.element;
    if (!element || this.finished || element.paused || typeof element.currentTime !== "number") return;
    if (Date.now() - this.lastMove > STALL_MS) void this.recover();
  }

  private recovering: Promise<boolean> | null = null;

  /** Reloads the same link from where the learner was, a few times, then gives up. */
  private recover(): Promise<boolean> {
    if (!this.recovering) {
      this.recovering = this.reload().finally(() => {
        this.recovering = null;
      });
    }
    return this.recovering;
  }

  private async reload(): Promise<boolean> {
    const element = this.element;
    while (element && !this.finished && this.retries < RETRIES) {
      this.retries += 1;
      await new Promise((resolve) => window.setTimeout(resolve, 1500 * this.retries));
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        await new Promise((resolve) => {
          const back = () => resolve(undefined);
          window.addEventListener("online", back, { once: true });
          window.setTimeout(back, 15000);
        });
      }
      if (this.finished) return false;
      const at = this.position;
      element.src = this.url;
      element.load?.();
      if (at > 0) {
        const seek = () => {
          try {
            element.currentTime = at;
          } catch {
            // not seekable yet: it starts again from the beginning of the piece
          }
        };
        if (element.readyState >= 1) seek();
        else element.addEventListener?.("loadedmetadata", seek, { once: true });
      }
      this.lastMove = Date.now();
      try {
        await element.play();
        return true;
      } catch {
        // tried again, up to RETRIES times
      }
    }
    this.end(false);
    return false;
  }

  private end(heard: boolean): void {
    if (this.finished) return;
    this.finished = true;
    this.cleanup();
    if (heard) this.handlers.onEnded();
    else this.handlers.onFailed();
  }

  private cleanup(): void {
    window.clearInterval(this.watch);
    document.removeEventListener("visibilitychange", this.visibility);
    const element = this.element;
    if (element) {
      element.onended = null;
      element.onerror = null;
      element.ontimeupdate = null;
    }
  }
}

/** The element of the single listening, read by the exam frame to show its time (D-173). */
export function examAudioElement(): HTMLAudioElement {
  return sharedElement();
}
